import { useCallback, useEffect, useRef, useState } from 'react';
import { AiActions } from '../../../Services/_actions/Ai/ai.actions.ts';
import type { StudentDeliverable, TeacherNotesAndPlan } from '../types/curriculum.types.ts';
import { fromBackendStudyPlanContent } from '../utils/curriculumMappers.ts';

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_MS = 90000;

type PollingStatus = 'idle' | 'polling' | 'complete' | 'timeout' | 'error';
export type AIPollingMode = 'study_plan' | 'teacher_notes' | 'deliverables';

export function useCurriculumAIPolling(
  studyPlanId: string | null,
  syllabusId: string | null,
  mode: AIPollingMode,
  enabled = false
) {
  const aiActions = AiActions();
  const [status, setStatus] = useState<PollingStatus>('idle');
  const [teacherNotes, setTeacherNotes] = useState<TeacherNotesAndPlan | null>(null);
  const [deliverables, setDeliverables] = useState<StudentDeliverable[]>([]);
  const [studyPlanWeeks, setStudyPlanWeeks] = useState<number>(0);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const poll = useCallback(async () => {
    const elapsed = Date.now() - startTimeRef.current;
    if (elapsed >= MAX_POLL_MS) {
      stopPolling();
      setStatus('timeout');
      return;
    }

    try {
      if (mode === 'study_plan') {
        if (!syllabusId) return;
        const plans = await aiActions.getStudyPlan(syllabusId);
        if (plans?.length > 0) {
          const plan = plans[0];
          const weeks = fromBackendStudyPlanContent(plan.content ?? {});
          if (weeks.length > 0) {
            setStudyPlanWeeks(weeks.length);
            setProgress(100);
            stopPolling();
            setStatus('complete');
            return;
          }
        }
        setProgress(Math.min(90, Math.floor((elapsed / MAX_POLL_MS) * 100)));
        return;
      }

      if (!studyPlanId) return;

      if (mode === 'teacher_notes') {
        const notesList = await aiActions.getTeacherNotes(studyPlanId);
        if (notesList?.length > 0) {
          const notes = notesList[0] as TeacherNotesAndPlan;
          if (notes.content?.length > 0) {
            setTeacherNotes(notes);
            setProgress(100);
            stopPolling();
            setStatus('complete');
            return;
          }
        }
        setProgress(Math.min(90, Math.floor((elapsed / MAX_POLL_MS) * 100)));
        return;
      }

      const notesList = await aiActions.getTeacherNotes(studyPlanId);
      if (notesList?.length > 0) {
        const notes = notesList[0] as TeacherNotesAndPlan;
        setTeacherNotes(notes);
        setProgress(50);

        const items = await aiActions.getStudentDeliverables(notes._id);
        if (items?.length > 0) {
          setDeliverables(items);
          setProgress(100);
          stopPolling();
          setStatus('complete');
          return;
        }
      }
    } catch {
      stopPolling();
      setStatus('error');
    }
  }, [aiActions, mode, studyPlanId, syllabusId, stopPolling]);

  useEffect(() => {
    if (!enabled) {
      stopPolling();
      return undefined;
    }

    if (mode === 'study_plan' && !syllabusId) return undefined;
    if (mode !== 'study_plan' && !studyPlanId) return undefined;

    setStatus('polling');
    setProgress(0);
    setTeacherNotes(null);
    setDeliverables([]);
    setStudyPlanWeeks(0);
    startTimeRef.current = Date.now();

    poll();
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => stopPolling();
  }, [enabled, mode, studyPlanId, syllabusId, poll, stopPolling]);

  return { status, teacherNotes, deliverables, studyPlanWeeks, progress };
}

/** @deprecated Use useCurriculumAIPolling with mode 'deliverables' */
export function useDeliverablesPolling(studyPlanId: string | null, enabled = false) {
  const result = useCurriculumAIPolling(studyPlanId, null, 'deliverables', enabled);
  return {
    status: result.status,
    teacherNotes: result.teacherNotes,
    deliverables: result.deliverables,
    progress: result.progress,
  };
}

/** Resolve syllabus → study plan → teacher notes → deliverables (no polling). */
export async function resolveCurriculumChain(
  knowledgeMapId: string,
  aiActions: ReturnType<typeof AiActions>
) {
  const syllabusList = await aiActions.getSyllabusList(knowledgeMapId);
  if (!syllabusList?.length) return { syllabus: null, studyPlan: null, teacherNotes: null, deliverables: [] };

  const syllabus = syllabusList[0];
  const plans = await aiActions.getStudyPlan(syllabus._id);
  if (!plans?.length) return { syllabus, studyPlan: null, teacherNotes: null, deliverables: [] };

  const studyPlan = plans[0];
  const notesList = await aiActions.getTeacherNotes(studyPlan._id);
  if (!notesList?.length) return { syllabus, studyPlan, teacherNotes: null, deliverables: [] };

  const teacherNotes = notesList[0];
  const deliverables = await aiActions.getStudentDeliverables(teacherNotes._id);
  return { syllabus, studyPlan, teacherNotes, deliverables: deliverables ?? [] };
}
